import React from 'react';
import { ExternalLink, Package, Clock, Shield, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const Rentals = () => {
  return (
    <section id="rentals" className="py-24 bg-[#f7f5f2] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-96 h-96 bg-[#6B3F3F] rounded-full blur-[120px]"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#d4af37] rounded-full blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1a1918] mb-4">
            Rent Photo Booths from Inkfly
          </h2>
          <p className="text-lg text-[#736c64] max-w-2xl mx-auto">
            Premium photo booth rental services backed by 13+ years of industry expertise
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
          {/* Info Section */}
          <div className="space-y-6">
            <Card className="border-2 border-[#6B3F3F]/30 hover-lift">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <img 
                    src="/assets/images/logos/inkfly-logo.png" 
                    alt="Inkfly Logo" 
                    className="h-20 w-auto object-contain"
                  />
                  <div>
                    <h3 className="text-2xl font-bold text-[#1a1918] mb-2">
                      Inkfly Photo Booth Rentals
                    </h3>
                    <p className="text-[#736c64]">
                      Your trusted partner for premium photo booth rentals
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Package className="w-6 h-6 text-[#6B3F3F] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[#1a1918] mb-1">Complete Packages</h4>
                      <p className="text-[#736c64] text-sm">
                        Full setup, operation, and tear-down included in every rental
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Clock className="w-6 h-6 text-[#6B3F3F] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[#1a1918] mb-1">Flexible Duration</h4>
                      <p className="text-[#736c64] text-sm">
                        Hourly, daily, or event-based rental options available
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Shield className="w-6 h-6 text-[#6B3F3F] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[#1a1918] mb-1">Professional Support</h4>
                      <p className="text-[#736c64] text-sm">
                        Trained attendants and 24/7 technical support throughout your event
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Star className="w-6 h-6 text-[#d4af37] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[#1a1918] mb-1">Premium Quality</h4>
                      <p className="text-[#736c64] text-sm">
                        All equipment regularly maintained and upgraded to latest standards
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-[#6B3F3F] to-[#8B5A5A] rounded-2xl p-12 text-white text-center hover-lift">
            <img 
              src="/assets/images/logos/inkfly-logo.png" 
              alt="Inkfly Logo" 
              className="h-28 w-auto object-contain mx-auto mb-6"
            />
            <h3 className="text-3xl font-bold mb-4">Ready to Rent?</h3>
            <p className="text-lg mb-8 opacity-90">
              Check out our wide range of photo booth options on rental
            </p>
            <a
              href="https://www.instagram.com/inkflyphotobooth/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full"
            >
              <Button
                size="lg"
                className="w-full bg-white text-[#6B3F3F] hover:bg-[#f7f5f2] font-semibold text-lg py-6 shine-effect"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Visit @inkflyphotobooth
              </Button>
            </a>
            <p className="text-sm mt-6 opacity-75">
              Or call us directly at +91 9841433422 / +91 9952075664
            </p>
          </div>
        </div>

        {/* Why Choose Section */}
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="text-center hover-lift border-2 border-[#d4af37]/30">
            <CardContent className="pt-8 pb-8">
              <div className="bg-[#d4af37] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-[#1a1918]">13+</span>
              </div>
              <h4 className="text-xl font-bold text-[#1a1918] mb-2">Years Experience</h4>
              <p className="text-[#736c64]">
                Trusted by thousands of events across India
              </p>
            </CardContent>
          </Card>
          <Card className="text-center hover-lift border-2 border-[#6B3F3F]/30">
            <CardContent className="pt-8 pb-8">
              <div className="bg-[#6B3F3F] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold text-[#1a1918] mb-2">All-Inclusive</h4>
              <p className="text-[#736c64]">
                Setup, props, attendants, and instant prints included
              </p>
            </CardContent>
          </Card>
          <Card className="text-center hover-lift border-2 border-[#d4af37]/30">
            <CardContent className="pt-8 pb-8">
              <div className="bg-[#d4af37] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#1a1918]" />
              </div>
              <h4 className="text-xl font-bold text-[#1a1918] mb-2">Insured & Licensed</h4>
              <p className="text-[#736c64]">
                Fully insured equipment with professional liability coverage
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Rentals;